import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch,
  arrayUnion,
} from 'firebase/firestore';
import { parseISO, startOfDay } from 'date-fns';
import { db } from '../lib/firebase';
import {
  FriendRequest,
  Friendship,
  FriendAccessLevel,
  PublicCruiseView,
  Notification,
} from '../types/friends';
import { sanitizeCruiseForFriend } from '../context/CruiseContext';
import { Cruise } from '../context/CruiseContext';
import { createNotification } from './notificationService';
import { debug } from '../utils/debug';
import { resolveEmailOrUsername, getLookupByEmail, getLookupByUid } from './friendLookupService';
import { removeUndefined, getTimestampMillis, getOptionalTimestampMillis } from '../utils/formatUtils';
import { formatFriendDisplayName, formatFriendHandle } from '../utils/friendDisplayUtils';

/**
 * Enriches friendship list with current email and username from friendLookup
 * so display stays correct when a friend changes their username or email.
 * Lookup is by uid (friendId) so it works regardless of account updates.
 */
export async function enrichFriendsWithCurrentUsernames(
  friends: Friendship[]
): Promise<Friendship[]> {
  if (friends.length === 0) return friends;
  const enriched = await Promise.all(
    friends.map(async (f) => {
      const lookup = await getLookupByUid(f.friendId);
      const currentEmail = lookup?.email ?? f.friendEmail;
      const currentUsername = lookup?.username ?? f.friendUsername ?? undefined;
      return { ...f, friendEmail: currentEmail, friendUsername: currentUsername };
    })
  );
  return enriched;
}

/**
 * Sends a friend request to a user by email or username (resolved via friendLookup only).
 */
export const sendFriendRequest = async (
  fromUserId: string,
  fromEmail: string,
  toEmailOrUsername: string,
  accessLevel: FriendAccessLevel
): Promise<string> => {
  try {
    debug.log(`[sendFriendRequest] Starting friend request from ${fromUserId} to: ${toEmailOrUsername}`);

    const toLookup = await resolveEmailOrUsername(toEmailOrUsername);
    if (!toLookup) {
      throw new Error(
        `User not found with "${toEmailOrUsername}". ` +
          `Please verify the email or username is correct and that they've set up their friend username.`
      );
    }
    const toUserId = toLookup.uid;
    const toEmailAddress = toLookup.email;
    const toUsername = toLookup.username ?? null;

    if (toUserId === fromUserId) {
      throw new Error('You cannot send a friend request to yourself');
    }

    const existingFriendshipRef = doc(db, 'friendships', fromUserId, 'friends', toUserId);
    const existingFriendshipSnap = await getDoc(existingFriendshipRef);
    if (existingFriendshipSnap.exists()) {
      throw new Error('You are already friends with this user');
    }

    const requestsRef = collection(db, 'friendRequests');
    const existingRequestQuery = query(
      requestsRef,
      where('fromUserId', '==', fromUserId),
      where('toUserId', '==', toUserId),
      where('status', '==', 'pending')
    );
    const existingRequestSnap = await getDocs(existingRequestQuery);
    if (!existingRequestSnap.empty) {
      throw new Error('Friend request already sent');
    }

    const fromLookup = await getLookupByEmail(fromEmail);
    const fromEmailVal = fromLookup?.email ?? fromEmail.toLowerCase().trim();
    const fromUsername = fromLookup?.username ?? null;

    const displayNameForNotification = fromUsername ? formatFriendHandle(fromUsername) : fromEmailVal;

    debug.log(`[sendFriendRequest] Creating friend request:`, {
      fromUserId,
      toUserId,
      fromEmail: fromEmailVal,
      fromUsername,
      toEmail: toEmailAddress,
      toUsername,
      accessLevel,
    });

    let requestRef;
    try {
      requestRef = await addDoc(requestsRef, {
        fromUserId,
        fromEmail: fromEmailVal,
        fromUsername: fromUsername ?? null,
        toUserId,
        toEmail: toEmailAddress,
        toUsername: toUsername ?? null,
        status: 'pending',
        accessLevel,
        createdAt: serverTimestamp(),
      });
      debug.log(`[sendFriendRequest] Friend request created successfully with ID: ${requestRef.id}`);
    } catch (createError: any) {
      console.error('[sendFriendRequest] Error creating friend request document:', createError);
      throw createError;
    }

    try {
      await createNotification(toUserId, {
        type: 'friend-request',
        fromUserId,
        fromDisplayName: displayNameForNotification,
        message: `${displayNameForNotification} sent you a friend request`,
        read: false,
        relatedRequestId: requestRef.id,
      });
      debug.log(`[sendFriendRequest] Notification created for user: ${toUserId}`);
    } catch (notificationError: any) {
      console.error('[sendFriendRequest] Error creating notification (non-critical):', notificationError);
    }

    return requestRef.id;
  } catch (error: any) {
    console.error('Error sending friend request:', error);
    throw error;
  }
};

/**
 * Gets friend requests for a user
 */
export const getFriendRequests = async (
  userId: string,
  status?: 'pending' | 'accepted' | 'rejected'
): Promise<FriendRequest[]> => {
  try {
    const requestsRef = collection(db, 'friendRequests');
    let q = query(
      requestsRef,
      where('toUserId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    if (status) {
      q = query(
        requestsRef,
        where('toUserId', '==', userId),
        where('status', '==', status),
        orderBy('createdAt', 'desc')
      );
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAt: getTimestampMillis(doc.data().createdAt),
      respondedAt: getOptionalTimestampMillis(doc.data().respondedAt),
    })) as FriendRequest[];
  } catch (error) {
    console.error('Error getting friend requests:', error);
    throw error;
  }
};

/**
 * Cancels a sent friend request
 * @param requestId The ID of the friend request
 * @param userId The ID of the user canceling (must be the sender)
 */
export const cancelFriendRequest = async (
  requestId: string,
  userId: string
): Promise<void> => {
  try {
    const requestRef = doc(db, 'friendRequests', requestId);
    const requestSnap = await getDoc(requestRef);

    if (!requestSnap.exists()) {
      throw new Error('Friend request not found');
    }

    const requestData = requestSnap.data() as FriendRequest;
    if (requestData.fromUserId !== userId) {
      throw new Error('You do not have permission to cancel this request');
    }

    if (requestData.status !== 'pending') {
      throw new Error('This friend request has already been responded to');
    }

    const batch = writeBatch(db);

    // Delete the request
    batch.delete(requestRef);

    // Delete the notification sent to the recipient
    const recipientNotificationsRef = collection(db, 'notifications', requestData.toUserId, 'notifications');
    const notificationQuery = query(
      recipientNotificationsRef,
      where('relatedRequestId', '==', requestId),
      where('type', '==', 'friend-request')
    );
    const notificationSnap = await getDocs(notificationQuery);
    notificationSnap.docs.forEach((notificationDoc) => {
      batch.delete(notificationDoc.ref);
    });

    await batch.commit();
  } catch (error: any) {
    console.error('Error canceling friend request:', error);
    throw error;
  }
};

/**
 * Responds to a friend request (accept or reject)
 * @param requestId The ID of the friend request
 * @param userId The ID of the user responding (must be the recipient)
 * @param accept Whether to accept or reject the request
 * @param yourAccessLevel The access level the accepting user wants to give the requester (only used if accept is true)
 */
export const respondToFriendRequest = async (
  requestId: string,
  userId: string,
  accept: boolean,
  yourAccessLevel: FriendAccessLevel
): Promise<void> => {
  try {
    const requestRef = doc(db, 'friendRequests', requestId);
    const requestSnap = await getDoc(requestRef);

    if (!requestSnap.exists()) {
      throw new Error('Friend request not found');
    }

    const requestData = requestSnap.data() as FriendRequest;
    if (requestData.toUserId !== userId) {
      throw new Error('You do not have permission to respond to this request');
    }

    if (requestData.status !== 'pending') {
      throw new Error('This friend request has already been responded to');
    }

    const batch = writeBatch(db);

    // Delete the related notification if it exists
    const notificationsRef = collection(db, 'notifications', userId, 'notifications');
    const notificationQuery = query(
      notificationsRef,
      where('relatedRequestId', '==', requestId),
      where('type', '==', 'friend-request')
    );
    const notificationSnap = await getDocs(notificationQuery);
    notificationSnap.docs.forEach((notificationDoc) => {
      batch.delete(notificationDoc.ref);
    });

    if (accept) {
      const requesterAccessLevel = requestData.accessLevel;
      const recipientAccessLevel = yourAccessLevel;

      batch.update(requestRef, {
        status: 'accepted',
        respondedAt: serverTimestamp(),
        requesterAccessLevel,
        recipientAccessLevel,
      });

      const fromEmail = requestData.fromEmail ?? '';
      const fromUsername = requestData.fromUsername ?? null;
      const toEmailAddress = requestData.toEmail ?? '';
      const toUsername = requestData.toUsername ?? null;

      const friendshipRef = doc(
        db,
        'friendships',
        requestData.toUserId,
        'friends',
        requestData.fromUserId
      );

      batch.set(friendshipRef, {
        friendId: requestData.fromUserId,
        accessLevel: recipientAccessLevel,
        createdAt: serverTimestamp(),
        friendEmail: fromEmail,
        friendUsername: fromUsername,
      });

      const reverseFriendshipRef = doc(
        db,
        'friendships',
        requestData.fromUserId,
        'friends',
        requestData.toUserId
      );

      batch.set(reverseFriendshipRef, {
        friendId: requestData.toUserId,
        accessLevel: requesterAccessLevel,
        createdAt: serverTimestamp(),
        friendEmail: toEmailAddress,
        friendUsername: toUsername,
      });
    } else {
      // Reject request
      batch.update(requestRef, {
        status: 'rejected',
        respondedAt: serverTimestamp(),
      });
    }

    await batch.commit();
  } catch (error: any) {
    console.error('Error responding to friend request:', error);
    throw error;
  }
};

/**
 * Gets all friends for a user, with display usernames resolved from friendLookup
 * so the latest username is always shown (e.g. after a friend changes it).
 */
export const getFriends = async (userId: string): Promise<Friendship[]> => {
  try {
    const friendsRef = collection(db, 'friendships', userId, 'friends');
    const snapshot = await getDocs(friendsRef);
    const raw = snapshot.docs.map((doc) => ({
      ...doc.data(),
    })) as Friendship[];
    return enrichFriendsWithCurrentUsernames(raw);
  } catch (error) {
    console.error('Error getting friends:', error);
    throw error;
  }
};

/**
 * Updates a friend's access level
 * Note: This only updates what access the friend has to the user's cruises.
 * The reverse friendship (what access the user has to the friend's cruises) is independent and should not be changed.
 */
export const updateFriendAccessLevel = async (
  userId: string,
  friendId: string,
  accessLevel: FriendAccessLevel
): Promise<void> => {
  try {
    // Only update the direct friendship: what access friendId has to userId's cruises
    const friendshipRef = doc(db, 'friendships', userId, 'friends', friendId);
    await updateDoc(friendshipRef, {
      accessLevel,
    });
  } catch (error) {
    console.error('Error updating friend access level:', error);
    throw error;
  }
};

/**
 * Removes a friend
 */
export const removeFriend = async (userId: string, friendId: string): Promise<void> => {
  try {
    const batch = writeBatch(db);

    // Delete both friendships
    const friendship1Ref = doc(db, 'friendships', userId, 'friends', friendId);
    const friendship2Ref = doc(db, 'friendships', friendId, 'friends', userId);

    batch.delete(friendship1Ref);
    batch.delete(friendship2Ref);

    // Remove friend from user's shared cruises
    // Note: We only clean up the user's own sharedCruises collection.
    // The friend's sharedCruises collection will be cleaned up when they remove the user,
    // or it will naturally become inaccessible since the friendship is deleted.
    const sharedCruisesRef = collection(db, 'sharedCruises', userId, 'cruises');
    const sharedCruisesSnap = await getDocs(sharedCruisesRef);
    sharedCruisesSnap.docs.forEach((docSnap) => {
      const cruiseData = docSnap.data();
      if (cruiseData.sharedWith?.includes(friendId)) {
        const updatedSharedWith = cruiseData.sharedWith.filter(
          (id: string) => id !== friendId
        );
        if (updatedSharedWith.length === 0) {
          batch.delete(docSnap.ref);
        } else {
          batch.update(docSnap.ref, {
            sharedWith: updatedSharedWith,
          });
        }
      }
    });

    await batch.commit();
  } catch (error) {
    console.error('Error removing friend:', error);
    throw error;
  }
};

/**
 * Gets cruises for a friend (handles access level logic)
 */
export const getFriendCruises = async (
  userId: string,
  friendId: string
): Promise<PublicCruiseView[]> => {
  try {
    // Get friendship to check access level
    debug.log('[getFriendCruises] Reading friendship', { userId, friendId });
    const friendshipRef = doc(db, 'friendships', userId, 'friends', friendId);
    const friendshipSnap = await getDoc(friendshipRef);

    if (!friendshipSnap.exists()) {
      throw new Error('You are not friends with this user');
    }

    const friendship = friendshipSnap.data() as Friendship;
    debug.log('[getFriendCruises] Friendship ok', {
      friendId,
      accessLevel: friendship.accessLevel,
    });
    const cruisesRef = collection(db, 'cruises');
    let cruises: Cruise[] = [];

    if (friendship.accessLevel === 'full-access') {
      // Get all cruises for friend
      debug.log('[getFriendCruises] Full-access: querying cruises where userId==friendId', {
        friendId,
      });
      const q = query(cruisesRef, where('userId', '==', friendId));
      const snapshot = await getDocs(q);
      debug.log('[getFriendCruises] Cruises query ok', {
        friendId,
        count: snapshot.docs.length,
      });
      cruises = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Cruise[];
    } else {
      // Get only shared cruises. Use a query with sharedWith array-contains so the list
      // operation is allowed by rules (list ops need query constraints that match the rule).
      debug.log('[getFriendCruises] Restricted-access: querying sharedCruises/{friendId}/cruises where sharedWith contains userId', {
        friendId,
      });
      const sharedCruisesRef = collection(db, 'sharedCruises', friendId, 'cruises');
      const sharedCruisesQuery = query(
        sharedCruisesRef,
        where('sharedWith', 'array-contains', userId)
      );
      const sharedCruisesSnap = await getDocs(sharedCruisesQuery);
      debug.log('[getFriendCruises] sharedCruises read ok', {
        friendId,
        docCount: sharedCruisesSnap.docs.length,
      });
      const sharedCruiseIds = sharedCruisesSnap.docs.map((doc) => doc.id);

      if (sharedCruiseIds.length > 0) {
        debug.log('[getFriendCruises] Fetching cruise docs by id', {
          friendId,
          cruiseIds: sharedCruiseIds,
        });
        const cruisePromises = sharedCruiseIds.map(async (cruiseId) => {
          const cruiseRef = doc(db, 'cruises', cruiseId);
          const cruiseSnap = await getDoc(cruiseRef);
          if (cruiseSnap.exists()) {
            return { id: cruiseSnap.id, ...cruiseSnap.data() } as Cruise;
          }
          return null;
        });
        cruises = (await Promise.all(cruisePromises)).filter(
          (cruise) => cruise !== null
        ) as Cruise[];
        debug.log('[getFriendCruises] Cruise docs ok', { friendId, count: cruises.length });
      }
    }

    // Sanitize cruises for friend viewing
    return cruises.map(sanitizeCruiseForFriend);
  } catch (error: any) {
    debug.log('[getFriendCruises] Permission/error occurred for friendId (see last log above for failing read)', {
      friendId,
      accessLevel: '(unknown if failure was before friendship read)',
    });
    console.error('Error getting friend cruises:', error);
    throw error;
  }
};

/**
 * Shares a cruise with a restricted-access friend
 */
export const shareCruiseWithFriend = async (
  userId: string,
  cruiseId: string,
  friendId: string
): Promise<void> => {
  try {
    // Verify friendship exists and is restricted-access
    const friendshipRef = doc(db, 'friendships', userId, 'friends', friendId);
    const friendshipSnap = await getDoc(friendshipRef);

    if (!friendshipSnap.exists()) {
      throw new Error('You are not friends with this user');
    }

    const friendship = friendshipSnap.data() as Friendship;
    if (friendship.accessLevel === 'full-access') {
      throw new Error('Full access friends automatically see all cruises');
    }

    // Verify cruise belongs to user
    const cruiseRef = doc(db, 'cruises', cruiseId);
    const cruiseSnap = await getDoc(cruiseRef);
    if (!cruiseSnap.exists() || cruiseSnap.data().userId !== userId) {
      throw new Error('Cruise not found or does not belong to you');
    }

    // Add to shared cruises
    const sharedCruiseRef = doc(db, 'sharedCruises', userId, 'cruises', cruiseId);
    const sharedCruiseSnap = await getDoc(sharedCruiseRef);

    if (sharedCruiseSnap.exists()) {
      // Update existing shared cruise
      const currentSharedWith = sharedCruiseSnap.data().sharedWith || [];
      if (!currentSharedWith.includes(friendId)) {
        await updateDoc(sharedCruiseRef, {
          sharedWith: arrayUnion(friendId),
        });
      }
    } else {
      // Create new shared cruise document
      await setDoc(sharedCruiseRef, {
        cruiseId,
        sharedWith: [friendId],
        sharedAt: serverTimestamp(),
      });
    }
  } catch (error: any) {
    console.error('Error sharing cruise with friend:', error);
    throw error;
  }
};

/**
 * Unshares a cruise with a restricted-access friend
 */
export const unshareCruiseWithFriend = async (
  userId: string,
  cruiseId: string,
  friendId: string
): Promise<void> => {
  try {
    const sharedCruiseRef = doc(db, 'sharedCruises', userId, 'cruises', cruiseId);
    const sharedCruiseSnap = await getDoc(sharedCruiseRef);

    if (!sharedCruiseSnap.exists()) {
      return; // Already not shared
    }

    const currentSharedWith = sharedCruiseSnap.data().sharedWith || [];
    const updatedSharedWith = currentSharedWith.filter((id: string) => id !== friendId);

    if (updatedSharedWith.length === 0) {
      // Delete the document if no friends left
      await deleteDoc(sharedCruiseRef);
    } else {
      // Update the document
      await updateDoc(sharedCruiseRef, {
        sharedWith: updatedSharedWith,
      });
    }
  } catch (error: any) {
    console.error('Error unsharing cruise with friend:', error);
    throw error;
  }
};

/**
 * Gets shared cruise IDs for a friend
 */
export const getSharedCruises = async (
  userId: string,
  friendId: string
): Promise<string[]> => {
  try {
    const sharedCruisesRef = collection(db, 'sharedCruises', userId, 'cruises');
    const snapshot = await getDocs(sharedCruisesRef);
    return snapshot.docs
      .filter((doc) => doc.data().sharedWith?.includes(friendId))
      .map((doc) => doc.id);
  } catch (error) {
    console.error('Error getting shared cruises:', error);
    throw error;
  }
};

/**
 * Gets the list of friend IDs with whom a specific cruise has been shared
 */
export const getCruiseSharedWith = async (
  userId: string,
  cruiseId: string
): Promise<string[]> => {
  try {
    const sharedCruiseRef = doc(db, 'sharedCruises', userId, 'cruises', cruiseId);
    const snap = await getDoc(sharedCruiseRef);
    if (!snap.exists()) return [];
    return (snap.data().sharedWith as string[]) || [];
  } catch (error) {
    console.error('Error getting cruise sharedWith:', error);
    return [];
  }
};

/**
 * Finds friends traveling on the same cruise
 */
export const getTravelingCompanions = async (
  userId: string,
  cruiseId: string,
  shipName: string,
  departureDate: string
): Promise<Friendship[]> => {
  try {
    // Get the cruise to verify it belongs to user
    const cruiseRef = doc(db, 'cruises', cruiseId);
    const cruiseSnap = await getDoc(cruiseRef);
    if (!cruiseSnap.exists() || cruiseSnap.data().userId !== userId) {
      return [];
    }

    // Get all friends
    const friends = await getFriends(userId);
    if (friends.length === 0) return [];

    // Check each friend's cruises in parallel
    const companionPromises = friends.map(async (friend) => {
      try {
        const friendCruises = await getFriendCruises(userId, friend.friendId);
        const matchingCruise = friendCruises.find(
          (cruise) =>
            cruise.shipName === shipName && cruise.departureDate === departureDate
        );

        return matchingCruise ? friend : null;
      } catch (error) {
        // Skip friends we can't access
        debug.warn(`Could not check cruises for friend ${friend.friendId}:`, error);
        return null;
      }
    });

    const results = await Promise.all(companionPromises);
    return results.filter((f): f is Friendship => f !== null);
  } catch (error) {
    console.error('Error getting traveling companions:', error);
    return [];
  }
};

/**
 * Aggregated cruise with friend information
 */
export interface AggregatedCruise {
  cruise: PublicCruiseView;
  friends: Array<{
    friendId: string;
    friendDisplayName: string;
    friendEmail: string;
    accessLevel: FriendAccessLevel;
  }>;
}

/**
 * Gets all friends' cruises aggregated together, grouped by ship and dates
 */
export const getAllFriendsCruises = async (
  userId: string,
  includePast: boolean = false
): Promise<AggregatedCruise[]> => {
  try {
    // Get all friends
    const friends = await getFriends(userId);
    
    if (friends.length === 0) {
      return [];
    }

    // Fetch cruises for all friends in parallel
    const friendCruisePromises = friends.map(async (friend) => {
      try {
        const cruises = await getFriendCruises(userId, friend.friendId);
        return cruises.map((cruise) => ({
          cruise,
          friend: {
            friendId: friend.friendId,
            friendDisplayName: formatFriendDisplayName(friend),
            friendEmail: friend.friendEmail,
            accessLevel: friend.accessLevel,
          },
        }));
      } catch (error) {
        // Skip friends we can't access (graceful degradation)
        debug.warn(`Could not fetch cruises for friend ${friend.friendId}:`, error);
        return [];
      }
    });

    const friendCruiseResults = await Promise.all(friendCruisePromises);
    
    // Flatten the results
    const allCruiseEntries = friendCruiseResults.flat();

    // Group cruises by ship name + departure date + return date
    const cruiseGroups = new Map<string, AggregatedCruise>();

    for (const entry of allCruiseEntries) {
      const groupKey = `${entry.cruise.cruiseLine}|${entry.cruise.shipName}|${entry.cruise.departureDate}|${entry.cruise.returnDate}`;
      
      if (cruiseGroups.has(groupKey)) {
        const existing = cruiseGroups.get(groupKey)!;
        const friendExists = existing.friends.some(
          (f) => f.friendId === entry.friend.friendId
        );
        if (!friendExists) {
          existing.friends.push(entry.friend);
        }
      } else {
        cruiseGroups.set(groupKey, {
          cruise: entry.cruise,
          friends: [entry.friend],
        });
      }
    }

    const aggregatedCruises = Array.from(cruiseGroups.values());
    
    if (includePast) {
      return aggregatedCruises;
    }

    const today = startOfDay(new Date());

    const relevantCruises = aggregatedCruises.filter((ac) => {
      const departureDate = startOfDay(parseISO(ac.cruise.departureDate));
      const returnDate = startOfDay(parseISO(ac.cruise.returnDate));

      return (today >= departureDate && today < returnDate) || departureDate >= today;
    });

    relevantCruises.sort((a, b) => {
      const departureDateA = startOfDay(parseISO(a.cruise.departureDate));
      const returnDateA = startOfDay(parseISO(a.cruise.returnDate));
      const isCurrentlySailingA = today >= departureDateA && today < returnDateA;

      const departureDateB = startOfDay(parseISO(b.cruise.departureDate));
      const returnDateB = startOfDay(parseISO(b.cruise.returnDate));
      const isCurrentlySailingB = today >= departureDateB && today < returnDateB;

      if (isCurrentlySailingA && !isCurrentlySailingB) return -1;
      if (!isCurrentlySailingA && isCurrentlySailingB) return 1;

      return departureDateA.getTime() - departureDateB.getTime();
    });

    return relevantCruises;
  } catch (error: any) {
    console.error('Error getting all friends cruises:', error);
    throw error;
  }
};

/**
 * Checks which friends have already received an active/pending cruise invite for a specific cruise
 */
export const getInvitedFriendIdsForCruise = async (
  userId: string,
  cruiseId: string,
  friendIds: string[]
): Promise<string[]> => {
  if (!friendIds || friendIds.length === 0) return [];
  try {
    const checks = friendIds.map(async (friendId) => {
      try {
        const notifsRef = collection(db, 'notifications', friendId, 'notifications');
        const q = query(
          notifsRef,
          where('fromUserId', '==', userId),
          where('cruiseId', '==', cruiseId),
          where('type', '==', 'cruise-invite')
        );
        const snap = await getDocs(q);
        return snap.empty ? null : friendId;
      } catch (err) {
        debug.warn(`Failed to check existing invite for friend ${friendId}:`, err);
        return null;
      }
    });

    const results = await Promise.all(checks);
    return results.filter((id): id is string => id !== null);
  } catch (error) {
    console.error('Error checking invited friend IDs for cruise:', error);
    return [];
  }
};

/**
 * Invites a friend to a cruise (sends a cruise-invite notification with sanitized itinerary data).
 * Checks beforehand if an active invite has already been sent to prevent duplicates.
 */
export const inviteFriendToCruise = async (
  userId: string,
  fromDisplayName: string,
  friendId: string,
  cruise: Cruise
): Promise<string> => {
  try {
    // 1. Primary check: If already recorded in cruise.invitedFriendIds on user's own cruise document
    if (cruise.invitedFriendIds && cruise.invitedFriendIds.includes(friendId)) {
      debug.log('[inviteFriendToCruise] Friend already recorded as invited on cruise:', friendId);
      return 'already-invited';
    }

    // 2. Secondary check: Query Firestore notifications for existing active invite
    try {
      const notifsRef = collection(db, 'notifications', friendId, 'notifications');
      const existingQuery = query(
        notifsRef,
        where('fromUserId', '==', userId),
        where('cruiseId', '==', cruise.id),
        where('type', '==', 'cruise-invite')
      );
      const existingSnap = await getDocs(existingQuery);
      if (!existingSnap.empty) {
        // Invite already exists! Ensure the cruise document tracks it and return existing notification ID
        try {
          await updateDoc(doc(db, 'cruises', cruise.id), {
            invitedFriendIds: arrayUnion(friendId),
          });
        } catch (e) {
          debug.warn('Could not update cruise invitedFriendIds:', e);
        }
        return existingSnap.docs[0].id;
      }
    } catch (checkErr) {
      debug.warn('[inviteFriendToCruise] Non-fatal: could not query friend notifications collection:', checkErr);
    }

    const sanitized = sanitizeCruiseForFriend(cruise);
    const notificationId = await createNotification(friendId, {
      type: 'cruise-invite',
      fromUserId: userId,
      fromDisplayName,
      message: `${fromDisplayName} invited you to join them on a cruise: ${cruise.shipName} (${cruise.departureDate})`,
      read: false,
      cruiseId: cruise.id,
      cruiseLine: cruise.cruiseLine,
      shipName: cruise.shipName,
      sailingDate: cruise.departureDate,
      inviteStatus: 'pending',
      invitedCruiseData: sanitized,
    });

    // Record invited friend ID on the cruise document
    try {
      await updateDoc(doc(db, 'cruises', cruise.id), {
        invitedFriendIds: arrayUnion(friendId),
      });
    } catch (e) {
      debug.warn('Could not update cruise invitedFriendIds:', e);
    }

    return notificationId;
  } catch (error: any) {
    console.error('Error inviting friend to cruise:', error);
    throw error;
  }
};

/**
 * Accepts a cruise invite notification and copies the itinerary into the user's cruises
 */
export const acceptCruiseInvite = async (
  userId: string,
  notification: Notification
): Promise<string> => {
  try {
    if (!notification.invitedCruiseData) {
      throw new Error('No itinerary details attached to this cruise invitation.');
    }

    const cruiseToCopy = { ...notification.invitedCruiseData };
    delete (cruiseToCopy as any).id;
    delete (cruiseToCopy as any).created;
    delete (cruiseToCopy as any).userId;
    
    const newCruisePayload = removeUndefined({
      ...cruiseToCopy,
      userId,
      created: Date.now(),
      completed: false,
    });

    const newCruiseRef = await addDoc(collection(db, 'cruises'), newCruisePayload);

    if (notification.id) {
      const notificationRef = doc(db, 'notifications', userId, 'notifications', notification.id);
      await deleteDoc(notificationRef);
    }

    return newCruiseRef.id;
  } catch (error: any) {
    console.error('Error accepting cruise invite:', error);
    throw error;
  }
};


